import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from '@node-space/ui-components'
import { ERROR_STATUS_CODES } from 'constants/General'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { Portfolio } from 'types/rewards'
import { generateReference } from 'utils/utils'
import { REWARDS_REFERENCE_PREFIX } from '../constants/const'
import type { RewardsWithdrawFormData, Wallet, WithdrawSource } from '../types'
import { invalidateRewardsQueries } from '../utils'
import { useRewardsCustomerWithdraw } from './api/useRewardsCustomerWithdraw'
import { useRewardsWithdraw } from './api/useRewardsWithdraw'

interface UseWithdrawMutationsProps {
  isRemoveAllocation: boolean
  userPortfolios: Portfolio[]
  selectedCurrency: string
  selectedWallet: Wallet | null
  onClose?: () => void
}

interface WithdrawParams {
  formData: RewardsWithdrawFormData
  withdrawSource: WithdrawSource
  twoFactorCode?: string
}

export const useWithdrawMutations = ({
  isRemoveAllocation,
  userPortfolios,
  selectedCurrency,
  selectedWallet,
  onClose,
}: UseWithdrawMutationsProps) => {
  const { t } = useTranslation()
  const { requestError, setRequestError } = useMappedErrorMessage()
  const reactQueryClient = useQueryClient()

  const { onRewardsWithdraw, isRewardsWithdrawPending } = useRewardsWithdraw({
    isRemoveAllocation,
    onSuccess: data => {
      const walletReference = userPortfolios.find(
        portfolio => portfolio.currency === data.currency
      )?.walletId

      invalidateRewardsQueries(reactQueryClient, walletReference)
      onClose?.()
      toast({
        variant: 'success',
        header: t(
          isRemoveAllocation
            ? 'rewards.removeAllocation.successNotificationTitle'
            : 'rewards.withdraw.successNotificationTitle',
          {
            walletName: selectedWallet?.name || '',
            currency: `${data.amount} ${data.currency}`,
          }
        ),
      })
    },
    onError: error => {
      setRequestError({
        show: true,
        errorCode: error?.status,
        handledMessage: isRemoveAllocation
          ? t('rewards.errors.removeAllocation')
          : t('rewards.errors.withdrawFromPortfolio'),
      })
    },
  })

  const { onRewardsCustomerWithdraw, isRewardsCustomerWithdrawPending } =
    useRewardsCustomerWithdraw({
      onSuccess: () => {
        invalidateRewardsQueries(reactQueryClient)
        onClose?.()
        toast({
          variant: 'success',
          header: t('rewards.withdraw.successNotificationTitle', {
            walletName: selectedWallet?.name || '',
            currency: selectedCurrency,
          }),
        })
      },
      onError: error => {
        setRequestError({
          show: true,
          errorCode: error?.status,
          handledMessage:
            error?.status === ERROR_STATUS_CODES.FORBIDDEN
              ? t('2faRejectedError')
              : t('rewards.errors.withdrawFromPortfolio'),
        })
      },
    })

  const withdraw = ({ formData, withdrawSource, twoFactorCode }: WithdrawParams) => {
    setRequestError(null)

    // Customer rewards withdrawal
    if (withdrawSource === 'customerRewards') {
      onRewardsCustomerWithdraw(
        {
          destinationWalletId: formData.withdrawToWallet?.value || '',
        },
        twoFactorCode
      )
      return
    }

    // Standard/Remove allocation withdrawal
    const portfolio = userPortfolios.find(p => p.currency === selectedCurrency)

    if (!portfolio) {
      setRequestError({
        show: true,
        errorCode: 400,
        handledMessage: t('rewards.withdraw.portfolioNotFound'),
      })
      return
    }

    onRewardsWithdraw({
      reference: generateReference(REWARDS_REFERENCE_PREFIX),
      portfolioId: portfolio.id,
      counterparty: {
        walletId: formData.withdrawToWallet?.value,
        amount: formData.amount,
      },
    })
  }

  return {
    onWithdraw: withdraw,
    isWithdrawPending: isRewardsWithdrawPending || isRewardsCustomerWithdrawPending,
    withdrawRequestError: requestError,
    setWithdrawRequestError: setRequestError,
  }
}
